import { StatusCode } from "api/protocols";
import { GetAccount } from "api/rpc/2024-04/facilityAdmin/client/accounts";
import Popup from "components/popup/Popup";
import { LocaleCurrency } from "helpers/Locale";
import { useAppSelector } from "hooks/redux";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { HouseAccountType } from "pages/secure/facility/customer/context/contextTypes";
import "./outstandingBalancePopup.scss";

export default function useOutstandingBalancePopup() {
  const { t } = useTranslation();
  const { facilityStore } = useAppSelector(store => store);
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState<HouseAccountType[]>([]);

  async function checkOutstandingBalance(customerId: number | string | null | undefined) {
    if (!facilityStore?.teeSheetSettings?.register_show_outstanding_balance || !customerId) {
      return;
    }

    const accountRes = await GetAccount({ customer_id: Number(customerId), closed: false }, false);
    if (accountRes.status !== StatusCode.OK || !accountRes.data) {
      return;
    }

    const owingAccounts = (accountRes.data as HouseAccountType[]).filter(
      account => Number(account.balance) > 0,
    );

    if (owingAccounts.length === 0) {
      return;
    }

    setAccounts(owingAccounts);
    setOpen(true);
  }

  function closeOutstandingBalancePopup() {
    setOpen(false);
    setAccounts([]);
  }

  const outstandingBalancePopup = (
    <Popup
      type="warning"
      open={open}
      title={t("Outstanding Balance")}
      description={t("This customer has an outstanding balance on the following account(s):")}
      onOk={closeOutstandingBalancePopup}
      okText={t("OK")}
      backDropCancel={false}
    >
      <div className="outstanding-balance-popup-accounts mt-4">
        {accounts.map(account => (
          <div key={account.id} className="outstanding-balance-popup-account-row mt-1">
            <span className="outstanding-balance-popup-account-title">{account.title}</span>
            <span className="outstanding-balance-popup-account-balance">
              <LocaleCurrency currency={account.currency} amount={account.balance} />
            </span>
          </div>
        ))}
      </div>
    </Popup>
  );

  return { checkOutstandingBalance, outstandingBalancePopup };
}
